/**
 * Flow360ViewerPanel — native 3D viewer (UVF) with entity dropdown and
 * layer visibility sidebar.
 *
 * - Searchable dropdown replaces the old entity card list
 * - Persistent ThreeViewer survives entity switches
 * - Camera + layer state saved/restored per entity for fast-switching
 * - Binary ArrayBuffer cache in resourceLoader skips re-fetches
 */

import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ManifestBundle } from "@flexcompute/uvf";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useProvenance } from "../../hooks/useProvenance";
import {
  fetchVisualizationUrls,
} from "../../flow360/api";
import { getManifestLayers, type ManifestLayer } from "../viewer/manifestHelpers";
import { LayerSidebar } from "../viewer/LayerSidebar";
import { FieldToolbar } from "../viewer/FieldToolbar";
import { IsosurfacePanel } from "../viewer/IsosurfacePanel";
import { SlicePanel } from "../viewer/SlicePanel";
import { CycleToolbar, type CycleStep } from "../viewer/CycleToolbar";
import { useRunTable } from "../../hooks/useRunTable";
import type { UVFViewerHandle, ViewerState, FieldState } from "../viewer/UVFViewer";
import { usePlanformStore } from "../../stores/planformStore";
import { PlanformControls } from "../viewer/PlanformControls";
import { setReferenceQuantity } from "../../api/projects";
import { clearResourceCache } from "../viewer/resourceLoader";

const UVFViewer = lazy(() => import("../viewer/UVFViewer"));

import "./Flow360ViewerPanel.css";

const PORTAL_BASE = (
  import.meta.env.VITE_FLOW360_LOGIN_URL ?? "https://my.dev-simulation.cloud/login"
).replace(/\/login\/?$/, "");

// ---------------------------------------------------------------------------
// Entity extraction
// ---------------------------------------------------------------------------

interface Props {
  projectId: string;
  branch?: string;
  rows?: readonly Record<string, unknown>[];
  compact?: boolean;
  selectionId?: string | null;
  entityTypes?: readonly string[];
}

type NodeMeta = Record<string, unknown> | undefined;

interface Flow360Entity {
  artifactId: string;
  label: string;
  name: string;
  url: string;
  entityType: string;
  entityId: string;
  vizReady: boolean;
  selectionIds: string[];
}

const ENTITY_MAP: {
  kind: string;
  idKey: string;
  label: string;
  pathSegment: string;
  entityType: string;
}[] = [
  { kind: "run",          idKey: "flow360_case_id",         label: "Case",         pathSegment: "flow360/cases",         entityType: "case" },
  { kind: "geometry",     idKey: "flow360_geometry_id",     label: "Geometry",     pathSegment: "flow360/geometries",     entityType: "geometry" },
  { kind: "surface_mesh", idKey: "flow360_surface_mesh_id", label: "Surface Mesh", pathSegment: "flow360/surfacemeshes",  entityType: "surface_mesh" },
  { kind: "volume_mesh",  idKey: "flow360_volume_mesh_id",  label: "Volume Mesh",  pathSegment: "flow360/volumemeshes",   entityType: "volume_mesh" },
];

function extractEntities(
  nodes: {
    id?: string;
    kind: string;
    name?: string;
    metadata?: NodeMeta;
    value_json?: NodeMeta;
  }[],
): Flow360Entity[] {
  const entities: Flow360Entity[] = [];
  for (const n of nodes) {
    const meta = n.metadata as Record<string, string> | undefined;
    if (!meta) continue;
    const vals = n.value_json as Record<string, string> | undefined;
    for (const e of ENTITY_MAP) {
      const flow360Id = meta[e.idKey];
      if (n.kind === e.kind && flow360Id) {
        entities.push({
          artifactId: n.id ?? "",
          label: e.label,
          name: n.name ?? flow360Id,
          url: `${PORTAL_BASE}/${e.pathSegment}/${flow360Id}`,
          entityType: e.entityType,
          entityId: flow360Id,
          vizReady:
            vals?.visualization_status === "completed" || e.kind === "run",
          selectionIds: [n.id, flow360Id].filter((value): value is string => Boolean(value)),
        });
      }
    }
  }
  return entities;
}

type RunTableEntitySpec = {
  label: string;
  pathSegment: string;
  entityType: string;
  idKeys: string[];
  nameKeys: string[];
  readyKeys: string[];
  defaultVizReady: boolean;
};

const RUN_TABLE_ENTITY_SPECS: RunTableEntitySpec[] = [
  {
    label: "Case",
    pathSegment: "flow360/cases",
    entityType: "case",
    idKeys: ["flow360_case_id", "case_id"],
    nameKeys: ["flow360_case_name", "case_name", "name", "runId"],
    readyKeys: ["visualization_status", "case_visualization_status"],
    defaultVizReady: true,
  },
  {
    label: "Geometry",
    pathSegment: "flow360/geometries",
    entityType: "geometry",
    idKeys: ["geom_flow360_geometry_id", "flow360_geometry_id", "geometry_id"],
    nameKeys: ["geom_name", "flow360_geometry_name", "geometry_name", "design"],
    readyKeys: ["geom_visualization_status", "visualization_status"],
    defaultVizReady: true,
  },
  {
    label: "Surface Mesh",
    pathSegment: "flow360/surfacemeshes",
    entityType: "surface_mesh",
    idKeys: ["surf_mesh_flow360_surface_mesh_id", "flow360_surface_mesh_id", "surface_mesh_id", "surf_mesh_id"],
    nameKeys: ["surf_mesh_name", "flow360_surface_mesh_name", "surface_mesh_name", "mesh"],
    readyKeys: ["surf_mesh_visualization_status", "visualization_status"],
    defaultVizReady: true,
  },
  {
    label: "Volume Mesh",
    pathSegment: "flow360/volumemeshes",
    entityType: "volume_mesh",
    idKeys: ["vol_mesh_flow360_volume_mesh_id", "flow360_volume_mesh_id", "volume_mesh_id", "vol_mesh_id"],
    nameKeys: ["vol_mesh_name", "flow360_volume_mesh_name", "volume_mesh_name", "mesh"],
    readyKeys: ["vol_mesh_visualization_status", "visualization_status"],
    defaultVizReady: true,
  },
];

function recordString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return null;
}

function firstRecordString(record: Record<string, unknown>, keys: readonly string[]): string | null {
  for (const key of keys) {
    const value = recordString(record, key);
    if (value) return value;
  }
  return null;
}

function rowSelectionIds(row: Record<string, unknown>, entityId: string): string[] {
  const ids = new Set<string>([entityId]);
  for (const key of [
    "_uvf_node_id",
    "uvf_node_id",
    "uvf_asset_id",
    "flowfield_asset_id",
    "volume_visualization_node_id",
    "visualization_node_id",
    "_flowfield_node_id",
    "flowfield_node_id",
    "_node_id",
    "node_id",
    "rowNodeId",
    "source_node_id",
    "runId",
    "name",
    "case_id",
    "flow360_case_id",
  ]) {
    const value = recordString(row, key);
    if (value) ids.add(value);
  }
  const cellNodeIds = row.cellNodeIds;
  if (cellNodeIds && typeof cellNodeIds === "object" && !Array.isArray(cellNodeIds)) {
    for (const value of Object.values(cellNodeIds)) {
      if (typeof value === "string" && value.trim()) ids.add(value);
    }
  }
  return [...ids];
}

function rowVizReady(row: Record<string, unknown>, spec: RunTableEntitySpec): boolean {
  for (const key of spec.readyKeys) {
    const value = recordString(row, key);
    if (!value) continue;
    const normalized = value.toLowerCase();
    if (normalized.includes("complete") || normalized.includes("ready") || normalized.includes("success")) return true;
    if (normalized.includes("fail") || normalized.includes("error") || normalized.includes("missing")) return false;
  }
  return spec.defaultVizReady;
}

function extractRunTableEntities(rows: readonly Record<string, unknown>[] | undefined): Flow360Entity[] {
  if (!rows?.length) return [];
  const entities: Flow360Entity[] = [];
  // FXC-8618 fix: a single Flow360 entity (e.g. one shared geometry) is referenced
  // by many run rows. We dedupe to one entity per entityType:flow360Id, but the
  // entity must carry EVERY referencing row's selection ids — otherwise selecting
  // a run whose row isn't the first to mention the shared geometry leaves its
  // (cell/row) selection id off the entity, so entityMatchesSelection() misses and
  // the Geometry preview shows "No Flow360 case for this row" while the per-run
  // (unique) case entity still resolves. So merge selectionIds + vizReady on repeat.
  //
  // PERF (FXC large-project hang): a shared geometry/mesh entity is referenced
  // by EVERY run row (X-57: one volume mesh → 2,837 runs). The previous merge
  // rebuilt `selectionIds = [...new Set([...existing, ...rowSelections])]` on
  // each repeat — copying an ever-growing array per row → O(N²) and a ~26s
  // main-thread freeze on the X-57 DAG. Accumulate selection ids into a per-key
  // Set instead (amortised O(1) inserts) and materialise the arrays once at the
  // end, keeping first-seen entity order.
  const byKey = new Map<string, Flow360Entity>();
  const selectionsByKey = new Map<string, Set<string>>();
  for (const row of rows) {
    for (const spec of RUN_TABLE_ENTITY_SPECS) {
      const flow360Id = firstRecordString(row, spec.idKeys);
      if (!flow360Id) continue;
      const dedupeKey = `${spec.entityType}:${flow360Id}`;
      const rowSelections = rowSelectionIds(row, flow360Id);
      const existing = byKey.get(dedupeKey);
      if (existing) {
        const accumulated = selectionsByKey.get(dedupeKey)!;
        for (const id of rowSelections) accumulated.add(id);
        existing.vizReady = existing.vizReady || rowVizReady(row, spec);
        continue;
      }
      const artifactId = firstRecordString(row, ["_node_id", "node_id", "rowNodeId", "source_node_id"]) ?? flow360Id;
      const entity: Flow360Entity = {
        artifactId,
        label: spec.label,
        name: firstRecordString(row, spec.nameKeys) ?? flow360Id,
        url: `${PORTAL_BASE}/${spec.pathSegment}/${flow360Id}`,
        entityType: spec.entityType,
        entityId: flow360Id,
        vizReady: rowVizReady(row, spec),
        selectionIds: rowSelections,
      };
      byKey.set(dedupeKey, entity);
      selectionsByKey.set(dedupeKey, new Set(rowSelections));
      entities.push(entity);
    }
  }
  // Materialise the accumulated selection-id sets once (cheap; one array per
  // distinct entity, not per row).
  for (const entity of entities) {
    const accumulated = selectionsByKey.get(`${entity.entityType}:${entity.entityId}`);
    if (accumulated) entity.selectionIds = [...accumulated];
  }
  return entities;
}

function mergeEntities(primary: readonly Flow360Entity[], fallback: readonly Flow360Entity[]): Flow360Entity[] {
  const merged = new Map<string, Flow360Entity>();
  for (const entity of [...primary, ...fallback]) {
    const key = `${entity.entityType}:${entity.entityId}`;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, entity);
      continue;
    }
    merged.set(key, {
      ...existing,
      selectionIds: [...new Set([...existing.selectionIds, ...entity.selectionIds])],
      vizReady: existing.vizReady || entity.vizReady,
    });
  }
  return [...merged.values()];
}

function entityMatchesSelection(entity: Flow360Entity, selectionId: string | null): boolean {
  if (!selectionId) return false;
  return entity.artifactId === selectionId || entity.entityId === selectionId || entity.selectionIds.includes(selectionId);
}

function selectedEntityIndexForSelection(
  entities: readonly Flow360Entity[],
  selectionId: string | null,
): number | null {
  if (selectionId) {
    const selectedIdx = entities.findIndex((entity) => entityMatchesSelection(entity, selectionId));
    return selectedIdx >= 0 ? selectedIdx : null;
  }

  const firstReadyIdx = entities.findIndex((entity) => entity.vizReady);
  return firstReadyIdx >= 0 ? firstReadyIdx : null;
}

export const flow360ViewerTestHooks = {
  extractEntities,
  extractRunTableEntities,
  mergeEntities,
  entityMatchesSelection,
  selectedEntityIndexForSelection,
  rowVizReady,
  selectVisualizationManifestKey,
  viewerStateCacheKey,
  sharedCameraStateForMode,
};

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

const ExternalLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
    <path
      d="M3 2a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1V9.5a.5.5 0 0 1 1 0V12a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h2.5a.5.5 0 0 1 0 1H3Zm6.5-1a.5.5 0 0 0 0 1H13l-5.15 5.15a.5.5 0 0 0 .7.7L13.7 2.7V5.5a.5.5 0 0 0 1 0V1.5a.5.5 0 0 0-.5-.5H9.5Z"
      fill="currentColor"
    />
  </svg>
);

// ---------------------------------------------------------------------------
// ViewerData cache (Layer 1 — API response cache)
// ---------------------------------------------------------------------------

interface ViewerData {
  urlMap: Record<string, string>;
  manifest: ManifestBundle;
  fetchedAt: number;
}

const MAX_VIEWER_DATA_ENTRIES = 10;
const VIEWER_DATA_CACHE_TTL_MS = 5 * 60 * 1000;
const viewerDataCache = new Map<string, ViewerData>();

function viewerDataCacheKey(entity: Flow360Entity): string {
  return `${entity.entityType}:${entity.entityId}`;
}

function invalidateViewerData(entity: Flow360Entity): void {
  viewerDataCache.delete(viewerDataCacheKey(entity));
}

function selectVisualizationManifestKey(files: Record<string, string>): string | null {
  const keys = Object.keys(files);
  return (
    keys.find((key) => key.toLowerCase().split("/").pop() === "manifest.json") ??
    keys.find((key) => {
      const leaf = key.toLowerCase().split("/").pop() ?? "";
      return leaf.includes("manifest") && leaf.endsWith(".json");
    }) ??
    keys.find((key) => key.toLowerCase().endsWith(".json")) ??
    null
  );
}

async function fetchOrCacheViewerData(
  entity: Flow360Entity,
  projectId: string,
): Promise<ViewerData> {
  const key = viewerDataCacheKey(entity);
  const cached = viewerDataCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < VIEWER_DATA_CACHE_TTL_MS) return cached;

  const result = await fetchVisualizationUrls(
    entity.entityType,
    entity.entityId,
    projectId,
  );

  const manifestKey = selectVisualizationManifestKey(result.files);
  if (!manifestKey) throw new Error("No manifest found in visualization data");

  const manifestUrl = result.files[manifestKey]!;
  const manifestResp = await fetch(manifestUrl);
  if (!manifestResp.ok) throw new Error("Failed to fetch manifest");
  const manifest: ManifestBundle = await manifestResp.json();

  const data: ViewerData = { urlMap: result.files, manifest, fetchedAt: Date.now() };
  // Evict oldest entry if at capacity
  if (viewerDataCache.size >= MAX_VIEWER_DATA_ENTRIES) {
    const oldest = viewerDataCache.keys().next().value;
    if (oldest !== undefined) viewerDataCache.delete(oldest);
  }
  viewerDataCache.set(key, data);
  return data;
}

type Flow360FieldState = FieldState & { wireframe?: boolean; lic?: boolean };
type PanelFieldState = { fieldName?: string; colormapName?: string };
type SharedViewerListener = (sourceId: symbol) => void;
type Flow360ViewerMode = "compact" | "full";

interface SharedFlow360ViewerState {
  stateByEntity: Map<string, ViewerState>;
  fieldByEntity: Map<string, Flow360FieldState>;
  panelFields: Map<string, PanelFieldState>;
  lastState?: ViewerState;
  lastStateMode?: Flow360ViewerMode;
  lastFieldState?: Flow360FieldState;
  fixBetween: boolean;
  refCount: number;
  listeners: Set<SharedViewerListener>;
}

const sharedViewerStates = new Map<string, SharedFlow360ViewerState>();

function createViewerSessionKey(projectId: string, branch: string, entityTypeKey: string): string {
  return [projectId, branch, entityTypeKey || "all"].join("\u001f");
}

function getSharedViewerState(key: string): SharedFlow360ViewerState {
  const existing = sharedViewerStates.get(key);
  if (existing) return existing;

  const state: SharedFlow360ViewerState = {
    stateByEntity: new Map(),
    fieldByEntity: new Map(),
    panelFields: new Map(),
    fixBetween: true,
    refCount: 0,
    listeners: new Set(),
  };
  sharedViewerStates.set(key, state);
  return state;
}

function hasViewerCamera(state: ViewerState | null | undefined): state is ViewerState {
  return Boolean(state?.camera?.cameraAndOrbit);
}

function viewerStateCacheKey(mode: Flow360ViewerMode, entityKey: string): string {
  return `${mode}:${entityKey}`;
}

function sharedCameraStateForMode(
  state: SharedFlow360ViewerState,
  mode: Flow360ViewerMode,
): ViewerState | undefined {
  return state.lastStateMode === mode ? state.lastState : undefined;
}

function hasFieldStateValues(state: Flow360FieldState | null | undefined): state is Flow360FieldState {
  return Boolean(
    state &&
      (state.fieldName ||
        state.colormapName ||
        state.range ||
        (state.contourSteps ?? 0) > 0 ||
        state.wireframe ||
        state.lic),
  );
}

function fieldStatesEqual(a: Flow360FieldState | undefined, b: Flow360FieldState | undefined): boolean {
  return (
    a?.fieldName === b?.fieldName &&
    a?.colormapName === b?.colormapName &&
    a?.range?.[0] === b?.range?.[0] &&
    a?.range?.[1] === b?.range?.[1] &&
    a?.contourSteps === b?.contourSteps &&
    a?.wireframe === b?.wireframe &&
    a?.lic === b?.lic
  );
}

function publishSharedViewerState(state: SharedFlow360ViewerState, sourceId: symbol) {
  for (const listener of state.listeners) listener(sourceId);
}

// ---------------------------------------------------------------------------
// Entity dropdown (searchable, grouped)
// ---------------------------------------------------------------------------

function EntityDropdown({
  entities,
  selectedIdx,
  onSelect,
}: {
  entities: Flow360Entity[];
  selectedIdx: number | null;
  onSelect: (idx: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const popoverRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const grouped = useMemo(() => {
    const q = search.toLowerCase();
    const filtered = search
      ? entities.filter((e) => e.name.toLowerCase().includes(q))
      : entities;
    const groups = new Map<string, { idx: number; entity: Flow360Entity }[]>();
    for (let i = 0; i < entities.length; i++) {
      const ent = entities[i]!;
      if (!filtered.includes(ent)) continue;
      const key = ent.label;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push({ idx: i, entity: ent });
    }
    return groups;
  }, [entities, search]);

  const active = selectedIdx !== null ? entities[selectedIdx] : null;

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={triggerRef}
        className="f3v-dropdown-trigger"
        onClick={() => setOpen((o) => !o)}
      >
        {active ? (
          <>
            <span className="f3v-entity-badge">{active.label}</span>
            <span className="f3v-dropdown-name">{active.name}</span>
          </>
        ) : (
          <span style={{ color: "var(--gray-9)" }}>Select entity...</span>
        )}
        <span style={{ fontSize: 9, marginLeft: "auto" }}>&#9662;</span>
      </button>

      {open && (
        <div ref={popoverRef} className="f3v-dropdown-popover">
          <div style={{ padding: "8px 8px 4px" }}>
            <input
              autoFocus
              placeholder="Search entities..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="f3v-dropdown-search"
            />
          </div>
          <div className="f3v-dropdown-list">
            {grouped.size === 0 && (
              <div className="f3v-dropdown-empty">No matching entities</div>
            )}
            {[...grouped.entries()].map(([label, items]) => (
              <div key={label}>
                <div className="f3v-dropdown-group-header">{label}s</div>
                {items.map(({ idx, entity }) => (
                  <button
                    key={entity.entityId}
                    className={`f3v-dropdown-item${idx === selectedIdx ? " f3v-dropdown-item--active" : ""}`}
                    onClick={() => {
                      onSelect(idx);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <span className="f3v-dropdown-item-name">
                      {entity.name}
                    </span>
                    {entity.vizReady ? (
                      <span className="f3v-dropdown-item-viz" title="Visualization ready">
                        &#9679;
                      </span>
                    ) : (
                      <span
                        className="f3v-dropdown-item-link"
                        title="Open in portal"
                      >
                        <ExternalLinkIcon />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Panel
// ---------------------------------------------------------------------------

export default function Flow360ViewerPanel({ projectId, branch: branchOverride, rows: rowsOverride, compact = false, selectionId, entityTypes }: Props) {
  const { currentBranch, selectedArtifactId, selectArtifact } =
    useProvenanceContext();
  const viewerMode: Flow360ViewerMode = compact ? "compact" : "full";
  const viewerBranch = branchOverride ?? currentBranch;
  const { data: provData, isLoading: provenanceLoading } = useProvenance(projectId, viewerBranch);
  const { data: runTableData, isLoading: runTableLoading } = useRunTable(projectId, viewerBranch);
  const effectiveSelectionId = selectionId ?? selectedArtifactId;
  const normalizedSelectionId = effectiveSelectionId ?? null;
  const entityTypeKey = entityTypes?.join("\u001f") ?? "";
  const viewerSessionKey = createViewerSessionKey(projectId, viewerBranch, entityTypeKey);
  const sharedViewerState = useMemo(
    () => getSharedViewerState(viewerSessionKey),
    [viewerSessionKey],
  );
  const panelInstanceId = useRef(Symbol("flow360-viewer-panel"));
  const entityTypeFilter = useMemo(() => new Set(entityTypeKey ? entityTypeKey.split("\u001f") : []), [entityTypeKey]);

  const entities = useMemo(() => {
    const dagEntities = provData?.nodes ? extractEntities(provData.nodes as never[]) : [];
    const tableEntities = extractRunTableEntities(rowsOverride ?? runTableData?.rows);
    const merged = mergeEntities(dagEntities, tableEntities);
    if (!entityTypeFilter.size) return merged;
    return merged.filter((entity) => entityTypeFilter.has(entity.entityType));
  }, [entityTypeFilter, provData?.nodes, rowsOverride, runTableData?.rows]);

  const selectedIdx = useMemo(
    () => selectedEntityIndexForSelection(entities, normalizedSelectionId),
    [entities, normalizedSelectionId],
  );
  const activeEntity =
    selectedIdx !== null ? entities[selectedIdx] ?? null : null;

  // Per-entity state caches (Layer 2 — camera + layer visibility).
  // Compact and focus viewers share this store so resizing the flowfield
  // feels like one continuous view instead of a remount.
  const stateCache = useRef(sharedViewerState.stateByEntity);
  const viewerRef = useRef<UVFViewerHandle | null>(null);
  const guardIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [viewerReady, setViewerReady] = useState(false);
  const viewerRefCallback = useCallback((node: UVFViewerHandle | null) => {
    viewerRef.current = node;
    setViewerReady(!!node);
  }, []);

  // Active manifest layers for the sidebar
  const [layers, setLayers] = useState<ManifestLayer[]>([]);
  const [layerVis, setLayerVis] = useState<Map<string, boolean>>(new Map());

  // Detail panel (isosurface or slice controls)
  const [openPanelId, setOpenPanelId] = useState<string | null>(null);
  const openPanelLayer = useMemo(
    () => (openPanelId ? layers.find((l) => l.id === openPanelId) ?? null : null),
    [openPanelId, layers],
  );
  const isIsosurfacePanel = openPanelLayer?.label?.toLowerCase().includes("isosurface") ?? false;
  const isSlicePanel = openPanelLayer?.label?.toLowerCase().includes("slice") ?? false;

  // Field / surface visualization state
  const [fieldNames, setFieldNames] = useState<string[]>([]);
  const [colormaps, setColormaps] = useState<string[]>([]);
  const [activeField, setActiveField] = useState<string | undefined>();
  const [activeColormap, setActiveColormap] = useState<string | undefined>();
  const [fieldRange, setFieldRange] = useState<[number, number] | null>(null);
  const [contourSteps, setContourSteps] = useState(0);
  const [wireframe, setWireframe] = useState(false);
  const [lic, setLic] = useState(false);

  // ---- Planform overlay ----
  const isSurfaceMesh = activeEntity?.entityType === "surface_mesh";
  const planformVisible = usePlanformStore((s) => s.visible);
  const planformData = usePlanformStore((s) => s.data);
  const planformToggle = usePlanformStore((s) => s.toggleVisible);
  const planformCanvasRef = useRef<HTMLElement | null>(null);

  // Auto-hide planform when switching away from a surface mesh
  useEffect(() => {
    if (!isSurfaceMesh && planformVisible) {
      planformToggle();
    }
  }, [isSurfaceMesh]); // eslint-disable-line react-hooks/exhaustive-deps

  const meshOpacity = usePlanformStore((s) => s.meshOpacity);

  // Resolve canvas element for PlanformControls projection
  useEffect(() => {
    if (!planformVisible) return;
    const el = panelRef.current?.querySelector("canvas") ?? null;
    planformCanvasRef.current = el;
  }, [planformVisible, viewerReady]);

  // Mesh opacity: apply when planform visible / slider changes, restore on hide
  useEffect(() => {
    if (!viewerRef.current) return;
    if (planformVisible) {
      viewerRef.current.setMeshOpacity(meshOpacity);
    } else {
      viewerRef.current.setMeshOpacity(1.0);
    }
  }, [planformVisible, meshOpacity]);

  const handlePlanformConfirm = useCallback(async () => {
    const s = usePlanformStore.getState();
    const src = "planform_overlay";
    const lu = "m";
    const areaUnit = `${lu}²`;
    const sGross = s.areaMode === "gross" ? s.polygonArea : s.backendSGross;
    const sNet = s.areaMode === "net" ? s.polygonArea : s.backendSNet;
    const refArea = s.areaMode === "net" ? sNet : sGross;
    const areaParams = new Set(["s_gross", "s_net", "ref_area"]);
    try {
      await setReferenceQuantity(projectId, "ref_area", refArea, areaUnit, src);
      const extras: [string, number][] = [
        ["s_gross", sGross],
        ["s_net", sNet],
        ["wing_span", s.span],
        ["root_chord", s.rootChord],
        ["tip_chord", s.tipChord],
        ["standard_mean_chord", s.smc],
      ];
      for (const m of s.measurements) {
        const dx = m.end[0] - m.start[0];
        const dy = m.end[1] - m.start[1];
        const len = Math.sqrt(dx * dx + dy * dy);
        if (len > 0) extras.push([m.label.replace(/\s+/g, "_").toLowerCase(), len]);
      }
      await Promise.all(
        extras.filter(([, v]) => v > 0).map(([p, v]) => setReferenceQuantity(projectId, p, v, areaParams.has(p) ? areaUnit : lu, src)),
      );
    } catch { /* best-effort */ }
    usePlanformStore.getState().toggleVisible();
  }, [projectId]);

  // Per-entity field state cache
  const fieldStateCache = useRef(sharedViewerState.fieldByEntity);

  // Per-panel (slice/iso) field state for cross-case persistence
  const panelFieldCacheRef = useRef(sharedViewerState.panelFields);

  useEffect(() => {
    stateCache.current = sharedViewerState.stateByEntity;
    fieldStateCache.current = sharedViewerState.fieldByEntity;
    panelFieldCacheRef.current = sharedViewerState.panelFields;
  }, [sharedViewerState]);

  // Keep heavy resource caches alive while either compact or expanded viewer
  // is mounted; their lightweight view state stays available for the session.
  useEffect(() => {
    sharedViewerState.refCount += 1;
    return () => {
      sharedViewerState.refCount = Math.max(0, sharedViewerState.refCount - 1);
      if (sharedViewerState.refCount === 0) {
        clearResourceCache();
        viewerDataCache.clear();
      }
    };
  }, [sharedViewerState]);

  // "Fix between cases" — carry ALL viz state (camera, field, colormap, etc.) when switching
  const [fixBetween, setFixBetweenState] = useState(() => sharedViewerState.fixBetween);
  const setFixBetween = useCallback(
    (next: boolean) => {
      sharedViewerState.fixBetween = next;
      setFixBetweenState(next);
      publishSharedViewerState(sharedViewerState, panelInstanceId.current);
    },
    [sharedViewerState],
  );
  useEffect(() => {
    setFixBetweenState(sharedViewerState.fixBetween);
  }, [sharedViewerState]);
  const fixBetweenRef = useRef(fixBetween);
  fixBetweenRef.current = fixBetween;

  // Freeze frame: opaque snapshot of previous case shown during load to
  // prevent the jarring flash to a bare blue mesh.
  const [freezeFrame, setFreezeFrame] = useState<string | null>(null);

  // ---- Cycle on variable ----
  const [cycleVariable, setCycleVariable] = useState<string | null>(null);
  const [cycleIdx, setCycleIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playSpeed, setPlaySpeed] = useState(3000);

  const vizReadyNodeIds = useMemo(
    () => new Set(entities.filter((e) => e.vizReady).map((e) => e.artifactId)),
    [entities],
  );

  // Internal fields to exclude from variable picker
  const INTERNAL_FIELDS = new Set([
    "_node_id", "name", "status", "sweep_group", "flow360_case_id",
    "geom_name", "surf_mesh_name", "vol_mesh_name",
  ]);

  const cycleVariables = useMemo(() => {
    if (!runTableData) return [];
    return runTableData.columns
      .map((c) => c.field)
      .filter((f) => {
        if (INTERNAL_FIELDS.has(f)) return false;
        // Must be numeric in at least one viz-ready row
        return runTableData.rows.some(
          (r) => vizReadyNodeIds.has(r._node_id as string) && typeof r[f] === "number",
        );
      });
  }, [runTableData, vizReadyNodeIds]);

  const cycleSteps: CycleStep[] = useMemo(() => {
    if (!cycleVariable || !runTableData) return [];
    return runTableData.rows
      .filter((r) => vizReadyNodeIds.has(r._node_id as string) && typeof r[cycleVariable] === "number")
      .map((r) => ({ nodeId: r._node_id as string, value: r[cycleVariable] as number }))
      .sort((a, b) => a.value - b.value);
  }, [cycleVariable, runTableData, vizReadyNodeIds]);

  // Sync cycleIdx when external selection changes
  useEffect(() => {
    if (!selectedArtifactId || cycleSteps.length === 0) return;
    const idx = cycleSteps.findIndex((s) => s.nodeId === selectedArtifactId);
    if (idx >= 0 && idx !== cycleIdx) setCycleIdx(idx);
  }, [selectedArtifactId, cycleSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCycleStep = useCallback(
    (idx: number) => {
      const step = cycleSteps[idx];
      if (!step) return;
      setCycleIdx(idx);
      const entIdx = entities.findIndex((e) => e.artifactId === step.nodeId);
      if (entIdx >= 0) {
        selectArtifact(step.nodeId);
      }
    },
    [cycleSteps, entities, selectArtifact],
  );

  const handleCycleVariableChange = useCallback(
    (variable: string | null) => {
      setCycleVariable(variable);
      setCycleIdx(0);
      setPlaying(false);
      if (variable) setFixBetween(true); // auto-enable camera lock
    },
    [setFixBetween],
  );

  const handlePlayToggle = useCallback(() => setPlaying((p) => !p), []);

  // Keyboard shortcuts for cycling (Left/Right arrows)
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!cycleVariable || cycleSteps.length === 0) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); handleCycleStep((cycleIdx - 1 + cycleSteps.length) % cycleSteps.length); }
      if (e.key === "ArrowRight") { e.preventDefault(); handleCycleStep((cycleIdx + 1) % cycleSteps.length); }
    };
    const el = panelRef.current;
    el?.addEventListener("keydown", handler);
    return () => el?.removeEventListener("keydown", handler);
  }, [cycleVariable, cycleSteps, cycleIdx, handleCycleStep]);

  // Loading / error
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current entity key for state save on switch
  const prevEntityKey = useRef<string | null>(null);

  const entityKey = activeEntity
    ? `${activeEntity.entityType}:${activeEntity.entityId}`
    : null;
  const entityStateKey = entityKey ? viewerStateCacheKey(viewerMode, entityKey) : null;

  const activeEntityKeyRef = useRef<string | null>(entityKey);
  const activeEntityStateKeyRef = useRef<string | null>(entityStateKey);
  useEffect(() => {
    activeEntityKeyRef.current = entityKey;
    activeEntityStateKeyRef.current = entityStateKey;
  }, [entityKey, entityStateKey]);

  const buildCurrentFieldState = useCallback(
    (): Flow360FieldState => ({
      fieldName: activeField,
      colormapName: activeColormap,
      range: fieldRange ?? undefined,
      contourSteps,
      wireframe,
      lic,
    }),
    [activeField, activeColormap, fieldRange, contourSteps, wireframe, lic],
  );

  const saveCurrentViewState = useCallback(
    (
      targetStateKey: string | null = activeEntityStateKeyRef.current,
      targetEntityKey: string | null = activeEntityKeyRef.current,
      publish = true,
    ) => {
      if (!targetStateKey || !viewerRef.current) return;
      const state = viewerRef.current.saveState();
      stateCache.current.set(targetStateKey, state);
      if (hasViewerCamera(state)) {
        sharedViewerState.lastState = state;
        sharedViewerState.lastStateMode = viewerMode;
      }

      const fieldState = buildCurrentFieldState();
      if (targetEntityKey && hasFieldStateValues(fieldState)) {
        fieldStateCache.current.set(targetEntityKey, fieldState);
        sharedViewerState.lastFieldState = fieldState;
      }

      if (publish) publishSharedViewerState(sharedViewerState, panelInstanceId.current);
    },
    [buildCurrentFieldState, sharedViewerState, viewerMode],
  );
  const saveCurrentViewStateRef = useRef(saveCurrentViewState);
  useEffect(() => {
    saveCurrentViewStateRef.current = saveCurrentViewState;
  }, [saveCurrentViewState]);

  const applyFieldStateToViewer = useCallback(
    (fieldState: Flow360FieldState | undefined) => {
      const viewer = viewerRef.current;
      if (!viewer || !hasFieldStateValues(fieldState)) return;

      const targetField = fieldState.fieldName && fieldNames.includes(fieldState.fieldName)
        ? fieldState.fieldName
        : undefined;
      if (targetField) {
        viewer.setField(targetField);
        setActiveField(targetField);
      }

      if (fieldState.colormapName) {
        viewer.setColormap(fieldState.colormapName);
        setActiveColormap(fieldState.colormapName);
      }

      if (fieldState.range) {
        viewer.setFieldRange(fieldState.range[0], fieldState.range[1]);
        setFieldRange(fieldState.range);
      } else if (targetField) {
        setFieldRange(viewer.getFieldRange());
      }

      const steps = fieldState.contourSteps ?? 0;
      viewer.setContourSteps(steps);
      setContourSteps(steps);

      const wf = fieldState.wireframe ?? false;
      viewer.setWireframe(wf);
      setWireframe(wf);

      const licOn = fieldState.lic ?? false;
      const switchedField = viewer.setLIC(licOn);
      setLic(licOn);
      if (switchedField) {
        setActiveField(switchedField);
        setFieldRange(viewer.getFieldRange());
      }
    },
    [fieldNames],
  );

  const applySharedViewState = useCallback(() => {
    const viewer = viewerRef.current;
    if (!viewer || !fixBetweenRef.current) return;

    const state = sharedCameraStateForMode(sharedViewerState, viewerMode);
    if (hasViewerCamera(state)) viewer.restoreCamera(state.camera);

    if (state?.layers) {
      for (const [modelId, visible] of state.layers) {
        viewer.setLayerVisible(modelId, visible);
      }
      setLayerVis(new Map(state.layers));
    }

    applyFieldStateToViewer(sharedViewerState.lastFieldState);
  }, [applyFieldStateToViewer, sharedViewerState, viewerMode]);

  useEffect(() => {
    const listener: SharedViewerListener = (sourceId) => {
      if (sourceId === panelInstanceId.current) return;
      setFixBetweenState(sharedViewerState.fixBetween);
      if (!sharedViewerState.fixBetween) return;
      window.setTimeout(applySharedViewState, 0);
    };
    sharedViewerState.listeners.add(listener);
    return () => {
      sharedViewerState.listeners.delete(listener);
    };
  }, [applySharedViewState, sharedViewerState]);

  useEffect(() => {
    if (!entityKey) return;
    const fieldState = buildCurrentFieldState();
    if (!hasFieldStateValues(fieldState)) return;
    const previousState = sharedViewerState.lastFieldState;
    fieldStateCache.current.set(entityKey, fieldState);
    sharedViewerState.lastFieldState = fieldState;
    if (!fieldStatesEqual(previousState, fieldState)) {
      publishSharedViewerState(sharedViewerState, panelInstanceId.current);
    }
  }, [buildCurrentFieldState, entityKey, sharedViewerState]);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const scheduleSave = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        saveCurrentViewStateRef.current();
      }, 120);
    };

    el.addEventListener("pointerup", scheduleSave);
    el.addEventListener("pointercancel", scheduleSave);
    el.addEventListener("wheel", scheduleSave, { passive: true });
    el.addEventListener("keyup", scheduleSave);
    return () => {
      if (timer) clearTimeout(timer);
      el.removeEventListener("pointerup", scheduleSave);
      el.removeEventListener("pointercancel", scheduleSave);
      el.removeEventListener("wheel", scheduleSave);
      el.removeEventListener("keyup", scheduleSave);
      saveCurrentViewStateRef.current();
    };
  }, [compact, viewerReady]);

  // Save state for previous entity, load new entity
  const loadedEntityKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeEntity?.vizReady || !viewerRef.current) return;

    // Capture current state BEFORE saving/switching so we can carry it
    // to the next entity when "Fix between cases" is on.
    const isFixed = fixBetweenRef.current;
    const currentFullState = isFixed && loadedEntityKeyRef.current
      ? viewerRef.current.saveState()
      : undefined;
    const sharedModeState = isFixed
      ? sharedCameraStateForMode(sharedViewerState, viewerMode)
      : undefined;
    const lockedFullState = hasViewerCamera(currentFullState)
      ? currentFullState
      : sharedModeState;
    const lockedCamera = lockedFullState?.camera;
    const lockedLayers = lockedFullState?.layers;
    const currentFieldState = buildCurrentFieldState();
    const lockedFieldState = isFixed
      ? hasFieldStateValues(currentFieldState)
        ? currentFieldState
        : sharedViewerState.lastFieldState
      : undefined;
    const lockedPanelFields = isFixed
      ? new Map(panelFieldCacheRef.current)
      : undefined;

    // Save previous entity state before switching
    if (prevEntityKey.current && prevEntityKey.current !== entityKey) {
      saveCurrentViewState(viewerStateCacheKey(viewerMode, prevEntityKey.current), prevEntityKey.current);

      // Freeze the current frame so the user doesn't see a bare mesh flash
      if (isFixed) {
        const img = viewerRef.current.captureCanvas();
        if (img) setFreezeFrame(img);
      }
    }
    prevEntityKey.current = entityKey;

    let cancelled = false;
    if (guardIntervalRef.current) { clearInterval(guardIntervalRef.current); guardIntervalRef.current = null; }
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        let data = await fetchOrCacheViewerData(activeEntity, projectId);
        if (cancelled) return;

        // When "Fix between cases" is on, carry camera + layer visibility.
        // Otherwise fall back to per-entity saved state (auto-zoom on first visit).
        const savedState = lockedCamera?.cameraAndOrbit
          ? { camera: lockedCamera, layers: lockedLayers } as ViewerState
          : entityStateKey
            ? stateCache.current.get(entityStateKey)
            : undefined;

        try {
          await viewerRef.current!.load(data.urlMap, data.manifest, savedState);
        } catch (loadError) {
          invalidateViewerData(activeEntity);
          data = await fetchOrCacheViewerData(activeEntity, projectId);
          if (cancelled) return;
          await viewerRef.current!.load(data.urlMap, data.manifest, savedState);
          if (loadError instanceof Error) {
            console.warn("Retried Flow360 viewer load after refreshing visualization URLs", loadError.message);
          }
        }
        if (cancelled) return;

        if (entityStateKey) {
          const postLoadState = viewerRef.current!.saveState();
          stateCache.current.set(entityStateKey, postLoadState);
          if (hasViewerCamera(postLoadState)) {
            sharedViewerState.lastState = postLoadState;
            sharedViewerState.lastStateMode = viewerMode;
            publishSharedViewerState(sharedViewerState, panelInstanceId.current);
          }
        }
        loadedEntityKeyRef.current = entityKey;

        setLayers(getManifestLayers(data.manifest));

        const vis = viewerRef.current!.getLayerVisibility();
        setLayerVis(vis);

        // Re-apply per-panel (slice/iso) field state for "Fix between cases"
        if (lockedPanelFields && lockedPanelFields.size > 0) {
          const newLayers = getManifestLayers(data.manifest);
          for (const [layerId, pf] of lockedPanelFields) {
            if (!pf.fieldName) continue;
            const lay = newLayers.find((l) => l.id === layerId);
            if (!lay?.children) continue;
            const ids = lay.children.map((c) => c.id);
            viewerRef.current!.setFieldForModels(ids, pf.fieldName);
            if (pf.colormapName) {
              viewerRef.current!.setColormapForModels(ids, pf.colormapName);
            }
          }
        }

        // Discover available fields and colormaps.
        // fc.fieldNames is lazily populated from S3 data — on first visit
        // it may be empty.  Poll until names appear or we give up.
        const maps = viewerRef.current!.getColormaps();
        setColormaps(maps);

        let fieldsApplied = false;

        const applyFields = (names: string[]) => {
          if (cancelled) return;
          setFieldNames(names);
          if (names.length > 0) fieldsApplied = true;

          const savedFieldState = hasFieldStateValues(lockedFieldState)
            ? lockedFieldState
            : entityKey
              ? fieldStateCache.current.get(entityKey)
              : undefined;

          const targetField = savedFieldState?.fieldName && names.includes(savedFieldState.fieldName)
            ? savedFieldState.fieldName
            : savedFieldState?.fieldName
              ? names[0]
              : undefined;

          if (targetField) {
            const vr = viewerRef.current!;
            vr.setField(targetField);
            setActiveField(targetField);

            const cmap = savedFieldState?.colormapName ?? maps[0];
            if (cmap) { vr.setColormap(cmap); setActiveColormap(cmap); }

            if (savedFieldState?.range) {
              vr.setFieldRange(savedFieldState.range[0], savedFieldState.range[1]);
              setFieldRange(savedFieldState.range);
            } else {
              setFieldRange(vr.getFieldRange());
            }

            const steps = savedFieldState?.contourSteps ?? 0;
            vr.setContourSteps(steps);
            setContourSteps(steps);

            const wf = savedFieldState?.wireframe ?? false;
            const licOn = savedFieldState?.lic ?? false;
            setWireframe(wf);
            setLic(licOn);
            if (wf || licOn) {
              setTimeout(() => {
                if (cancelled) return;
                if (wf) vr.setWireframe(true);
                if (licOn) vr.setLIC(true);
              }, 300);
            }
          } else if (names.length > 0) {
            const firstField = names[0]!;
            viewerRef.current!.setField(firstField);
            setActiveField(firstField);
            setActiveColormap(maps[0]);
            setFieldRange(viewerRef.current!.getFieldRange());
            setContourSteps(0);
            setWireframe(false);
            setLic(false);
          } else {
            setActiveField(undefined);
            setActiveColormap(undefined);
            setFieldRange(null);
            setContourSteps(0);
            setWireframe(false);
            setLic(false);
          }

          // Deferred per-panel (slice/iso) field re-application — field data
          // is now confirmed available, so setFieldForModels will succeed.
          if (lockedPanelFields && lockedPanelFields.size > 0 && names.length > 0) {
            const panelLayers = getManifestLayers(data.manifest);
            for (const [layerId, pf] of lockedPanelFields) {
              if (!pf.fieldName) continue;
              const lay = panelLayers.find((l) => l.id === layerId);
              if (!lay?.children) continue;
              const ids = lay.children.map((c) => c.id);
              viewerRef.current!.setFieldForModels(ids, pf.fieldName);
              if (pf.colormapName) {
                viewerRef.current!.setColormapForModels(ids, pf.colormapName);
              }
            }
          }
        };

        const initialNames = viewerRef.current!.getFieldNames();
        if (initialNames.length > 0) {
          applyFields(initialNames);
        } else {
          if (!isFixed) applyFields([]);
          let fieldPollCount = 0;
          const pollFields = () => {
            if (cancelled || fieldPollCount >= 12 || !viewerRef.current) return;
            fieldPollCount++;
            setTimeout(() => {
              if (cancelled || !viewerRef.current) return;
              const names = viewerRef.current?.getFieldNames() ?? [];
              if (names.length > 0) {
                applyFields(names);
              } else {
                pollFields();
              }
            }, 500);
          };
          pollFields();
        }

        // Guard camera and freeze frame: poll until field retries finish,
        // restoring camera on each tick to counteract any auto-focus
        // triggered by assignFieldToInstance.  Drop freeze frame only once
        // the field is actually rendered.
        const guardCamera = lockedCamera?.cameraAndOrbit;
        let guardTicks = 0;
        const MAX_GUARD_TICKS = 20; // 20 × 300ms = 6s safety cap
        guardIntervalRef.current = setInterval(() => {
          if (cancelled) { clearInterval(guardIntervalRef.current!); guardIntervalRef.current = null; return; }
          guardTicks++;
          if (guardCamera) viewerRef.current?.restoreCamera(lockedCamera);
          const retriesDone = viewerRef.current?.isFieldRetryDone() ?? true;
          const ready = fieldsApplied && retriesDone;
          // Also drop if no fields were ever found after a generous wait
          const gaveUp = !fieldsApplied && guardTicks >= 10;
          if (ready || gaveUp || guardTicks >= MAX_GUARD_TICKS) {
            clearInterval(guardIntervalRef.current!);
            guardIntervalRef.current = null;
            if (guardCamera) viewerRef.current?.restoreCamera(lockedCamera);
            saveCurrentViewState(entityStateKey, entityKey);
            setTimeout(() => { if (!cancelled) setFreezeFrame(null); }, 400);
          }
        }, 300);

      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (guardIntervalRef.current) { clearInterval(guardIntervalRef.current); guardIntervalRef.current = null; }
    };
  }, [entityKey, activeEntity?.vizReady, viewerReady]);  // eslint-disable-line react-hooks/exhaustive-deps

  const handleEntitySelect = useCallback(
    (idx: number) => {
      const e = entities[idx];
      if (!e) return;
      if (e.vizReady) {
        selectArtifact(e.artifactId || e.entityId);
      } else {
        window.open(e.url, "_blank", "noopener");
      }
    },
    [entities, selectArtifact],
  );

  const handleLayerToggle = useCallback(
    (modelId: string, visible: boolean) => {
      viewerRef.current?.setLayerVisible(modelId, visible);
      setLayerVis((prev) => {
        const next = new Map(prev);
        next.set(modelId, visible);
        return next;
      });
    },
    [],
  );

  const handleOpenPanel = useCallback(
    (layerId: string) => {
      setOpenPanelId((prev) => (prev === layerId ? null : layerId));
    },
    [],
  );

  const handleClosePanel = useCallback(() => setOpenPanelId(null), []);

  const handlePanelFieldApplied = useCallback(
    (layerId: string, fieldName: string | undefined, colormapName: string | undefined) => {
      if (fieldName) {
        panelFieldCacheRef.current.set(layerId, { fieldName, colormapName });
      } else {
        panelFieldCacheRef.current.delete(layerId);
      }
    },
    [],
  );

  // ---- Field toolbar handlers ----

  const handleFieldChange = useCallback(
    (name: string | undefined) => {
      viewerRef.current?.setField(name);
      setActiveField(name);
      if (name) {
        const range = viewerRef.current?.getFieldRange() ?? null;
        setFieldRange(range);
      } else {
        setFieldRange(null);
      }
    },
    [],
  );

  const handleColormapChange = useCallback(
    (name: string) => {
      viewerRef.current?.setColormap(name);
      setActiveColormap(name);
    },
    [],
  );

  const handleRangeChange = useCallback(
    (min: number, max: number) => {
      viewerRef.current?.setFieldRange(min, max);
      setFieldRange([min, max]);
    },
    [],
  );

  const handleContourStepsChange = useCallback(
    (steps: number) => {
      viewerRef.current?.setContourSteps(steps);
      setContourSteps(steps);
    },
    [],
  );

  const handleWireframeToggle = useCallback(() => {
    const next = !wireframe;
    viewerRef.current?.setWireframe(next);
    setWireframe(next);
  }, [wireframe]);

  const handleLICToggle = useCallback(() => {
    const next = !lic;
    const switchedField = viewerRef.current?.setLIC(next) ?? null;
    setLic(next);
    if (switchedField) {
      setActiveField(switchedField);
      const range = viewerRef.current?.getFieldRange() ?? null;
      setFieldRange(range);
    }
  }, [lic]);

  const openPortal = useCallback(() => {
    window.open(PORTAL_BASE, "_blank", "noopener");
  }, []);

  // ---- Empty states ----

  if (entities.length === 0 && (provenanceLoading || (runTableLoading && !rowsOverride?.length))) {
    if (compact) {
      return (
        <div className="f3v-panel f3v-panel--compact f3v-panel--compact-empty">
          <span className="f3v-spinner" />
          <p>Loading Flow360 view</p>
        </div>
      );
    }
    return (
      <div className="f3v-panel f3v-panel--empty">
        <span className="f3v-spinner" />
        <p className="f3v-empty-title">Loading Flow360 entities</p>
        <p className="f3v-empty-desc">
          Linking the selected Thread rows to cases, meshes, and geometries.
        </p>
      </div>
    );
  }

  if (entities.length === 0) {
    if (compact) {
      return (
        <div className="f3v-panel f3v-panel--compact f3v-panel--compact-empty">
          <p>No linked Flow360 view</p>
          <span>Import or attach the entity to this row.</span>
        </div>
      );
    }
    return (
      <div className="f3v-panel f3v-panel--empty">
        <div className="f3v-empty-icon">
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5z" />
            <path d="M2 17l10 5 10-5" />
            <path d="M2 12l10 5 10-5" />
          </svg>
        </div>
        <p className="f3v-empty-title">No Flow360 entities</p>
        <p className="f3v-empty-desc">
          Run a simulation to see linked cases, meshes, and geometries here.
        </p>
        <button className="f3v-btn f3v-btn--primary" onClick={openPortal}>
          <ExternalLinkIcon />
          Open Flow360 Portal
        </button>
      </div>
    );
  }

  const selectionMiss =
    normalizedSelectionId !== null &&
    !entities.some((entity) => entityMatchesSelection(entity, normalizedSelectionId));

  if (selectionMiss) {
    if (compact) {
      return (
        <div className="f3v-panel f3v-panel--compact f3v-panel--compact-empty">
          <p>No Flow360 case for this row</p>
          <span>Select a row with a linked case.</span>
        </div>
      );
    }

    return (
      <div className="f3v-panel" ref={panelRef} tabIndex={-1}>
        <div className="f3v-toolbar">
          <EntityDropdown
            entities={entities}
            selectedIdx={selectedIdx}
            onSelect={handleEntitySelect}
          />
          <button
            className="f3v-toolbar-btn"
            title="Open portal"
            onClick={openPortal}
          >
            <ExternalLinkIcon />
          </button>
        </div>
        <div className="f3v-panel--empty" style={{ display: "flex", flex: "1 1 auto", flexDirection: "column" }}>
          <p className="f3v-empty-title">No Flow360 case for this selection</p>
          <p className="f3v-empty-desc">
            The selected row is not linked to a ready Flow360 case. Pick another run or refresh the run table.
          </p>
          <button className="f3v-btn f3v-btn--primary" onClick={openPortal}>
            <ExternalLinkIcon />
            Open Flow360 Portal
          </button>
        </div>
      </div>
    );
  }

  if (!activeEntity || !activeEntity.vizReady) {
    const unavailableTitle = activeEntity
      ? "Visualization not ready"
      : "No ready Flow360 visualization";
    const unavailableBody = activeEntity
      ? `${activeEntity.label} ${activeEntity.name} is linked, but Flow360 has no completed visualization for it yet.`
      : "The linked Flow360 entities for this selection are not ready to render.";

    if (compact) {
      return (
        <div className="f3v-panel f3v-panel--compact f3v-panel--compact-empty">
          <p>{unavailableTitle}</p>
          <span>{unavailableBody}</span>
        </div>
      );
    }

    return (
      <div className="f3v-panel" ref={panelRef} tabIndex={-1}>
        <div className="f3v-toolbar">
          <EntityDropdown
            entities={entities}
            selectedIdx={selectedIdx}
            onSelect={handleEntitySelect}
          />
          <button
            className="f3v-toolbar-btn"
            title="Open portal"
            onClick={openPortal}
          >
            <ExternalLinkIcon />
          </button>
        </div>
        <div className="f3v-panel--empty" style={{ display: "flex", flex: "1 1 auto", flexDirection: "column" }}>
          <p className="f3v-empty-title">{unavailableTitle}</p>
          <p className="f3v-empty-desc">{unavailableBody}</p>
          <button
            className="f3v-btn f3v-btn--primary"
            onClick={() => window.open(activeEntity?.url ?? PORTAL_BASE, "_blank", "noopener")}
          >
            <ExternalLinkIcon />
            Open Flow360 Portal
          </button>
        </div>
      </div>
    );
  }

  // ---- Main layout ----

  if (compact) {
    return (
      <div className="f3v-panel f3v-panel--compact" ref={panelRef} tabIndex={-1}>
        {activeEntity ? (
          <div className="f3v-compact-status">
            <span>{activeEntity.label}</span>
            <strong>{activeEntity.name}</strong>
          </div>
        ) : null}
        <div className="f3v-viewer-area">
          {error && (
            <div className="f3v-viewer-error">
              <p>Visualization unavailable</p>
            </div>
          )}

          <Suspense
            fallback={
              <div className="f3v-viewer-loading">
                <span className="f3v-spinner" />
              </div>
            }
          >
            <UVFViewer ref={viewerRefCallback} navCubeSize={72} />
          </Suspense>

          {freezeFrame ? (
            <img
              src={freezeFrame}
              alt=""
              className="f3v-freeze-frame"
              draggable={false}
            />
          ) : null}

          {loading && !freezeFrame ? (
            <div className="f3v-viewer-loading">
              <span className="f3v-spinner" />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="f3v-panel" ref={panelRef} tabIndex={-1}>
      <div className="f3v-toolbar">
        <EntityDropdown
          entities={entities}
          selectedIdx={selectedIdx}
          onSelect={handleEntitySelect}
        />
        <label className="f3v-lock-view" title="Keep camera, field coloring, and all settings when switching cases">
          <input
            type="checkbox"
            checked={fixBetween}
            onChange={(e) => {
              setFixBetween(e.target.checked);
              if (!e.target.checked) setFreezeFrame(null);
            }}
          />
          Fix between cases
        </label>
        <button
          className="f3v-toolbar-btn"
          title="Open portal"
          onClick={openPortal}
        >
          <ExternalLinkIcon />
        </button>
      </div>

      {/* Field toolbar — always rendered once fields are known to reserve layout space */}
      {fieldNames.length > 0 && (
        <div style={loading ? { opacity: 0.5, pointerEvents: "none" } : undefined}>
          <FieldToolbar
            fieldNames={fieldNames}
            colormaps={colormaps}
            activeField={activeField}
            activeColormap={activeColormap}
            range={fieldRange}
            contourSteps={contourSteps}
            wireframe={wireframe}
            lic={lic}
            planform={planformVisible}
            hasPlanformData={!!planformData && isSurfaceMesh}
            onFieldChange={handleFieldChange}
            onColormapChange={handleColormapChange}
            onRangeChange={handleRangeChange}
            onContourStepsChange={handleContourStepsChange}
            onWireframeToggle={handleWireframeToggle}
            onLICToggle={handleLICToggle}
            onPlanformToggle={planformToggle}
          />
        </div>
      )}

      {/* Standalone planform toggle when no fields (e.g. surface mesh without results) */}
      {fieldNames.length === 0 && !!planformData && isSurfaceMesh && (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "4px 8px", borderBottom: "1px solid var(--gray-a4)",
          background: "var(--color-background)",
        }}>
          <button
            style={{
              padding: "2px 6px", borderRadius: 4, fontSize: 11, cursor: "pointer",
              fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 4,
              ...(planformVisible
                ? { background: "var(--accent-9)", color: "white", border: "1px solid var(--accent-9)" }
                : { background: "transparent", color: "var(--gray-11)", border: "1px solid var(--gray-a6)" }),
            }}
            onClick={planformToggle}
            title="Toggle planform analysis overlay"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 20L8 4h8l4 16H4z" />
              <line x1="4" y1="20" x2="20" y2="20" />
            </svg>
            Planform
          </button>
        </div>
      )}

      {/* Cycle toolbar — step through sweep cases by variable */}
      {cycleVariables.length > 0 && (
        <CycleToolbar
          variables={cycleVariables}
          activeVariable={cycleVariable}
          onVariableChange={handleCycleVariableChange}
          steps={cycleSteps}
          currentIdx={cycleIdx}
          onStep={handleCycleStep}
          playing={playing}
          onPlayToggle={handlePlayToggle}
          speed={playSpeed}
          onSpeedChange={setPlaySpeed}
          loading={loading}
        />
      )}

      {/* 3D viewer area with layer sidebar overlay */}
      <div className="f3v-viewer-area">
        {error && (
          <div className="f3v-viewer-error">
            <p>Visualization unavailable</p>
            {activeEntity && (
              <button
                className="f3v-btn f3v-btn--small"
                onClick={() => window.open(activeEntity.url, "_blank", "noopener")}
              >
                <ExternalLinkIcon /> Open in Flow360
              </button>
            )}
          </div>
        )}

        <Suspense
          fallback={
            <div className="f3v-viewer-loading">
              <span className="f3v-spinner" />
              Loading viewer...
            </div>
          }
        >
          <UVFViewer ref={viewerRefCallback} />
        </Suspense>

        {/* Freeze frame — holds previous case's image during load to avoid flash */}
        {freezeFrame && (
          <>
            <img
              src={freezeFrame}
              alt=""
              className="f3v-freeze-frame"
              draggable={false}
            />
            {loading && (
              <div className="f3v-corner-loading">
                <span className="f3v-spinner" />
              </div>
            )}
          </>
        )}

        {/* Full loading overlay only when no freeze frame */}
        {loading && !freezeFrame && (
          <div className="f3v-viewer-loading">
            <span className="f3v-spinner" />
            Loading visualization...
          </div>
        )}

        {layers.length > 0 && !openPanelId && (
          <LayerSidebar
            layers={layers}
            visibility={layerVis}
            onToggle={handleLayerToggle}
            onOpenPanel={handleOpenPanel}
          />
        )}

        {openPanelLayer && isIsosurfacePanel && (
          <IsosurfacePanel
            layer={openPanelLayer}
            visibility={layerVis}
            onToggle={handleLayerToggle}
            onClose={handleClosePanel}
            viewerHandle={viewerRef.current}
            onFieldApplied={(f, c) => handlePanelFieldApplied(openPanelLayer.id, f, c)}
          />
        )}

        {openPanelLayer && isSlicePanel && (
          <SlicePanel
            layer={openPanelLayer}
            visibility={layerVis}
            onToggle={handleLayerToggle}
            onClose={handleClosePanel}
            viewerHandle={viewerRef.current}
            onFieldApplied={(f, c) => handlePanelFieldApplied(openPanelLayer.id, f, c)}
          />
        )}

        {planformVisible && planformData && isSurfaceMesh && (
          <PlanformControls
            viewer={viewerRef.current?.getViewer?.() ?? null}
            canvasEl={planformCanvasRef.current}
            onConfirm={handlePlanformConfirm}
          />
        )}
      </div>
    </div>
  );
}
